import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import type { MarkerRepository } from '../../application/MarkerRepository'
import type { FieldMarkerTask } from '../../domain/fieldMarkerTask'
import type { MarkerTask, MarkerTaskHistoryEntry } from '../../domain/markerTask'

const pendingMarkerTaskRowSchema = z.object({
  task_id: z.string(),
  contract_id: z.string(),
  contract_number: z.string().nullable(),
  service_location_id: z.string(),
  address_line1: z.string(),
  address_line2: z.string().nullable(),
  city: z.string().nullable(),
  status: z.literal('PENDING'),
  // Ajoutés par 20261008010000 : optionnels pour que le front reste valide avant la migration.
  client_name: z.string().nullable().optional(),
  client_phone: z.string().nullable().optional(),
  client_mobile_phone: z.string().nullable().optional(),
  client_email: z.string().nullable().optional(),
  service_postal_code: z.string().nullable().optional(),
  service_formatted_address: z.string().nullable().optional(),
  billing_address: z.string().nullable().optional(),
})

const historyMarkerTaskRowSchema = z.object({
  task_id: z.string(),
  contract_id: z.string(),
  contract_number: z.string().nullable(),
  service_location_id: z.string(),
  address_line1: z.string(),
  address_line2: z.string().nullable(),
  city: z.string().nullable(),
  status: z.enum(['INSTALLED', 'CANCELLED']),
  completed_at: z.string(),
})

const fieldMarkerTaskRowSchema = z.object({
  taskId: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  clientName: z.string().nullable(),
  phone: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  instructions: z.string().nullable(),
  lastAttemptAt: z.string().nullable(),
  lastAttemptReason: z.string().nullable(),
})

const completeResultSchema = z.object({
  taskId: z.string(),
  status: z.literal('INSTALLED'),
})

interface SupabaseResponse {
  data: unknown
  error: { message: string; code?: string } | null
}

function asResponse(value: unknown): SupabaseResponse {
  return value as SupabaseResponse
}

export function createSupabaseMarkerRepository(client: AppSupabaseClient): MarkerRepository {
  return {
    async list() {
      const response = asResponse(
        await client
          .from('pending_contract_marker_tasks_v1')
          .select(
            'task_id,contract_id,contract_number,service_location_id,address_line1,address_line2,city,status,client_name,client_phone,client_mobile_phone,client_email,service_postal_code,service_formatted_address,billing_address',
          ),
      )
      if (response.error) throw new Error(`MARKER_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (!Array.isArray(response.data)) throw new Error('MARKER_LIST_RESPONSE_INVALID')

      const tasks: MarkerTask[] = []
      for (const candidate of response.data) {
        const parsed = pendingMarkerTaskRowSchema.safeParse(candidate)
        if (!parsed.success) {
          reportUnexpectedError(parsed.error, { feature: 'markers', operation: 'map-list-row' })
          continue
        }
        tasks.push({
          taskId: parsed.data.task_id,
          contractId: parsed.data.contract_id,
          contractNumber: parsed.data.contract_number ?? '',
          serviceLocationId: parsed.data.service_location_id,
          addressLine1: parsed.data.address_line1,
          addressLine2: parsed.data.address_line2,
          city: parsed.data.city,
          status: parsed.data.status,
          clientName: parsed.data.client_name ?? null,
          clientPhone: parsed.data.client_phone ?? null,
          clientMobilePhone: parsed.data.client_mobile_phone ?? null,
          clientEmail: parsed.data.client_email ?? null,
          postalCode: parsed.data.service_postal_code ?? null,
          formattedAddress: parsed.data.service_formatted_address ?? null,
          billingAddress: parsed.data.billing_address ?? null,
        })
      }
      return tasks
    },
    async listField() {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(await rpcClient.rpc('field_marker_tasks_v1'))
      if (response.error) throw new Error(`MARKER_FIELD_LIST_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (!Array.isArray(response.data)) throw new Error('MARKER_FIELD_LIST_RESPONSE_INVALID')
      const tasks: FieldMarkerTask[] = []
      for (const candidate of response.data) {
        const parsed = fieldMarkerTaskRowSchema.safeParse(candidate)
        if (!parsed.success) {
          // Une ligne illisible est signalée, jamais masquée en silence derrière un faux compteur.
          reportUnexpectedError(parsed.error, { feature: 'markers', operation: 'map-field-row' })
          throw new Error('MARKER_FIELD_LIST_ROW_INVALID')
        }
        tasks.push(parsed.data)
      }
      return tasks
    },
    async history() {
      const response = asResponse(
        await client
          .from('contract_marker_task_history_v1')
          .select(
            'task_id,contract_id,contract_number,service_location_id,address_line1,address_line2,city,status,completed_at',
          )
          .order('completed_at', { ascending: false }),
      )
      if (response.error) throw new Error(`MARKER_HISTORY_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      if (!Array.isArray(response.data)) throw new Error('MARKER_HISTORY_RESPONSE_INVALID')

      const entries: MarkerTaskHistoryEntry[] = []
      for (const candidate of response.data) {
        const parsed = historyMarkerTaskRowSchema.safeParse(candidate)
        if (!parsed.success) {
          reportUnexpectedError(parsed.error, { feature: 'markers', operation: 'map-history-row' })
          continue
        }
        entries.push({
          taskId: parsed.data.task_id,
          contractId: parsed.data.contract_id,
          contractNumber: parsed.data.contract_number ?? '',
          serviceLocationId: parsed.data.service_location_id,
          addressLine1: parsed.data.address_line1,
          addressLine2: parsed.data.address_line2,
          city: parsed.data.city,
          status: parsed.data.status,
          completedAt: parsed.data.completed_at,
        })
      }
      return entries
    },
    async complete(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('complete_contract_marker_task_v1', {
          p_command: { idempotencyKey: command.idempotencyKey, taskId: command.taskId },
        }),
      )
      if (response.error) {
        throw new Error(
          response.error.message || `MARKER_COMPLETE_FAILED:${response.error.code ?? 'UNKNOWN'}`,
        )
      }
      const parsed = completeResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('MARKER_COMPLETE_RESPONSE_INVALID')
    },
    async reportFailure(command) {
      const rpcClient = client as unknown as SupabaseClient
      const response = asResponse(
        await rpcClient.rpc('report_contract_marker_failure_v1', {
          p_command: {
            idempotencyKey: command.idempotencyKey,
            taskId: command.taskId,
            reason: command.reason,
          },
        }),
      )
      if (response.error) {
        throw new Error(response.error.message || `MARKER_FAILURE_FAILED:${response.error.code ?? 'UNKNOWN'}`)
      }
    },
  }
}
